import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Box,
  Avatar,
  Tooltip,
  Stack,
  Button,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  Select,
  SelectChangeEvent,
  Chip,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import SettingsIcon from "@mui/icons-material/Settings";
import AccountCircle from "@mui/icons-material/AccountCircle";
import useAuth from "../../hooks/useAuth";
import { Link } from "react-router-dom";

interface HeaderProps {
  drawerWidth: number;
  onMobileOpen?: () => void;
  onDesktopCollapse?: () => void;
}

const Header: React.FC<HeaderProps> = ({ drawerWidth, onMobileOpen, onDesktopCollapse }) => {
  const {
    admin,
    logout,
    kitchens,
    selectedKitchenId,
    setSelectedKitchenId,
  } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  // Status state
  const [status, setStatus] = useState<"online" | "offline">("online");
  const [statusAnchor, setStatusAnchor] = useState<null | HTMLElement>(null);

  // Notifications state
  const [notificationsAnchor, setNotificationsAnchor] =
    useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<
    Array<{ id: number; text: string; type: string; time: string }>
  >([]);

  // Feedback state
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackRating, setFeedbackRating] = useState(5);

  const selectedKitchen = kitchens.find((kitchen) => kitchen._id === selectedKitchenId) || null;
  const showKitchenSelector = kitchens.length > 1;
  const showKitchenChip = kitchens.length === 1;

  const handleKitchenChange = (event: SelectChangeEvent<string>) => {
    setSelectedKitchenId(event.target.value || null);
  };
  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleClose();
  };

  // Status Handlers
  const handleStatusClick = (event: React.MouseEvent<HTMLElement>) => {
    setStatusAnchor(event.currentTarget);
  };

  const handleStatusClose = (newStatus?: "online" | "offline") => {
    if (newStatus) setStatus(newStatus);
    setStatusAnchor(null);
  };

  // Notifications Handlers
  const handleNotificationsClick = (event: React.MouseEvent<HTMLElement>) => {
    setStatusAnchor(null); // Close other anchors
    setNotificationsAnchor(event.currentTarget);
  };

  const handleNotificationsClose = () => {
    setNotificationsAnchor(null);
  };

  const handleClearNotifications = () => {
    setNotifications([]);
    setNotificationsAnchor(null);
  };

  // Get initials for avatar
  const getInitials = () => {
    if (!admin?.name)
      return admin?.email?.substring(0, 2).toUpperCase() || "AD";
    const parts = admin.name.split(" ");
    if (parts.length > 1) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return parts[0].substring(0, 2).toUpperCase();
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        width: "100%",
        backgroundColor: "white",
        color: "#0f172a",
        borderBottom: "1px solid #e2e8f0",
        boxShadow: "none",
        borderRadius: 0,
        zIndex: (theme) => theme.zIndex.drawer + 1, // keep above sidebar
      }}
    >
      <Toolbar
        sx={{
          justifyContent: "space-between",
          minHeight: "64px",
          px: { xs: 2, sm: 3 },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={onMobileOpen}
            sx={{ mr: 0.5, display: { sm: "none" } }}
          >
            <MenuIcon />
          </IconButton>
          
          <IconButton
            color="inherit"
            aria-label="collapse sidebar"
            edge="start"
            onClick={onDesktopCollapse}
            sx={{ mr: 0.5, display: { xs: "none", sm: "flex" }, color: "#64748b" }}
          >
            <MenuIcon />
          </IconButton>

          {/* Kinnow Logo */}
          <Box
            component={Link}
            to="/dashboard"
            sx={{
              display: "flex",
              alignItems: "center",
              textDecoration: "none",
            }}
          >
            <img
              src="/images/kinnow.png"
              alt="Kinnow"
              style={{ height: "28px", width: "auto" }}
            />
          </Box>

          {/* Greeting/Status - hidden on extra small screens */}
          <Box
            sx={{
              display: { xs: "none", lg: "flex" },
              alignItems: "center",
              gap: 2,
              ml: 2,
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: "#cbd5e1", fontWeight: 500 }}
            >
              |
            </Typography>
            {/* <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: "#475569" }}
            >
              Hello, {admin?.name?.split(" ")[0] || "Admin"}! 👋
            </Typography> */}
            <Typography
              variant="body2"
              sx={{ color: "#cbd5e1", fontWeight: 500 }}
            >
              •
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#64748b", fontWeight: 500, fontSize:20 }}
            >
              {new Date().toLocaleDateString("en-US", {
                weekday: "short",
                month: "short",
                day: "numeric",
              })}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={2.5}>
          {/* Kitchen Selector / Chip */}
          {showKitchenSelector && (
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <Select
                value={selectedKitchenId || ""}
                onChange={handleKitchenChange}
                displayEmpty
                sx={{
                  color: "#1e293b",
                  borderColor: "#e2e8f0",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e2e8f0",
                  },
                  "& .MuiSvgIcon-root": {
                    color: "#64748b",
                  },
                }}
              >
                {kitchens.map((kitchen) => (
                  <MenuItem key={kitchen._id} value={kitchen._id}>
                    {kitchen.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {showKitchenChip && selectedKitchen && (
            <Chip
              label={selectedKitchen.name}
              sx={{
                backgroundColor: "#f1f5f9",
                color: "#1e293b",
                fontWeight: 600,
              }}
            />
          )}

          {/* Share Feedback Button */}
          {/* <Button 
            onClick={() => setFeedbackOpen(true)}
            sx={{ 
              color: '#475569', 
              fontSize: '0.85rem', 
              fontWeight: 600,
              textTransform: 'none', 
              display: { xs: 'none', md: 'block' },
              '&:hover': { color: '#f97316', backgroundColor: 'transparent' } 
            }}
          >
            Share feedback
          </Button> */}

          {/* Online/Offline Status Switcher */}
          {/* <Box>
            <Button
              onClick={handleStatusClick}
              variant="outlined"
              size="small"
              sx={{
                textTransform: 'none',
                borderColor: status === 'online' ? '#a7f3d0' : '#fecaca',
                backgroundColor: status === 'online' ? '#f0fdf4' : '#fef2f2',
                color: status === 'online' ? '#15803d' : '#b91c1c',
                fontWeight: 700,
                fontSize: '0.8rem',
                borderRadius: '6px',
                px: 1.5,
                py: 0.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                '&:hover': {
                  backgroundColor: status === 'online' ? '#dcfce7' : '#fee2e2',
                  borderColor: status === 'online' ? '#86efac' : '#fca5a5',
                }
              }}
            >
              <Box 
                sx={{ 
                  width: 8, 
                  height: 8, 
                  borderRadius: '50%', 
                  backgroundColor: status === 'online' ? '#22c55e' : '#ef4444',
                  boxShadow: status === 'online' ? '0 0 0 0 rgba(34, 197, 94, 0.7)' : 'none',
                  animation: status === 'online' ? 'pulse 2s infinite' : 'none',
                  '@keyframes pulse': {
                    '0%': {
                      transform: 'scale(0.95)',
                      boxShadow: '0 0 0 0 rgba(34, 197, 94, 0.7)',
                    },
                    '70%': {
                      transform: 'scale(1)',
                      boxShadow: '0 0 0 6px rgba(34, 197, 94, 0)',
                    },
                    '100%': {
                      transform: 'scale(0.95)',
                      boxShadow: '0 0 0 0 rgba(34, 197, 94, 0)',
                    },
                  }
                }} 
              />
              {status === 'online' ? 'Online' : 'Offline'}
              <span style={{ fontSize: '0.65rem', marginLeft: '4px', opacity: 0.7 }}>▼</span>
            </Button>

            <Menu
              anchorEl={statusAnchor}
              open={Boolean(statusAnchor)}
              onClose={() => handleStatusClose()}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              sx={{
                mt: 1,
                '& .MuiPaper-root': {
                  borderRadius: 2,
                  boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)',
                  border: '1px solid #e2e8f0',
                  p: 0.5,
                  minWidth: '130px'
                }
              }}
            >
              <MenuItem 
                onClick={() => handleStatusClose('online')}
                sx={{ 
                  fontSize: '0.85rem', 
                  fontWeight: 600, 
                  color: '#15803d',
                  gap: 1.5,
                  borderRadius: 1.5,
                  my: 0.5
                }}
              >
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#22c55e' }} />
                Online
              </MenuItem>
              <MenuItem 
                onClick={() => handleStatusClose('offline')}
                sx={{ 
                  fontSize: '0.85rem', 
                  fontWeight: 600, 
                  color: '#b91c1c',
                  gap: 1.5,
                  borderRadius: 1.5,
                  my: 0.5
                }}
              >
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#ef4444' }} />
                Offline
              </MenuItem>
            </Menu>
          </Box> */}

          {/* Notifications bell with badge */}
          <Box>
            {/* <IconButton 
              size="small" 
              onClick={handleNotificationsClick}
              sx={{ 
                color: '#64748b', 
                backgroundColor: '#f8fafc', 
                p: 1, 
                border: '1px solid #e2e8f0',
                '&:hover': {
                  backgroundColor: '#f1f5f9'
                }
              }}
            >
              <Badge badgeContent={notifications.length} color="error" overlap="circular">
                <NotificationsNoneIcon sx={{ fontSize: 20 }} />
              </Badge>
            </IconButton> */}

            <Menu
              anchorEl={notificationsAnchor}
              open={Boolean(notificationsAnchor)}
              onClose={handleNotificationsClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              sx={{
                mt: 1.5,
                "& .MuiPaper-root": {
                  width: "320px",
                  borderRadius: 3,
                  border: "1px solid #e2e8f0",
                  boxShadow:
                    "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
                  p: 1,
                },
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderBottom: "1px solid #f1f5f9",
                  mb: 1,
                }}
              >
                <Typography
                  sx={{ fontWeight: 700, fontSize: "0.9rem", color: "#0f172a" }}
                >
                  Notifications
                </Typography>
                {notifications.length > 0 && (
                  <Button
                    size="small"
                    onClick={handleClearNotifications}
                    sx={{
                      fontSize: "0.75rem",
                      textTransform: "none",
                      color: "#f97316",
                    }}
                  >
                    Clear all
                  </Button>
                )}
              </Box>

              {notifications.length === 0 ? (
                <Box sx={{ py: 3, textAlign: "center" }}>
                  <Typography variant="body2" color="text.secondary">
                    No new notifications
                  </Typography>
                </Box>
              ) : (
                notifications.map((notif) => (
                  <MenuItem
                    key={notif.id}
                    onClick={handleNotificationsClose}
                    sx={{
                      whiteSpace: "normal",
                      borderRadius: 2,
                      py: 1,
                      my: 0.5,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.8rem",
                        fontWeight: 500,
                        color: "#1e293b",
                      }}
                    >
                      {notif.text}
                    </Typography>
                    <Typography
                      sx={{ fontSize: "0.65rem", color: "#94a3b8", mt: 0.5 }}
                    >
                      {notif.time}
                    </Typography>
                  </MenuItem>
                ))
              )}
            </Menu>
          </Box>

          {/* Settings Icon */}
          {/* <IconButton 
            size="small" 
            sx={{ 
              color: '#64748b', 
              backgroundColor: '#f8fafc', 
              p: 1, 
              border: '1px solid #e2e8f0',
              display: { xs: 'none', md: 'inline-flex' },
              '&:hover': {
                backgroundColor: '#f1f5f9'
              }
            }}
          >
            <SettingsIcon sx={{ fontSize: 20 }} />
          </IconButton> */}

          {/* User Profile dropdown */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Tooltip title="Account settings">
              <Button
                onClick={handleMenu}
                sx={{
                  textTransform: "none",
                  color: "inherit",
                  p: 0.5,
                  borderRadius: "24px",
                  border: "1px solid #e2e8f0",
                  backgroundColor: "#f8fafc",
                  "&:hover": {
                    backgroundColor: "#f1f5f9",
                  },
                }}
              >
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  sx={{ pl: 0.5, pr: 1.5 }}
                >
                  <Avatar
                    sx={{
                      width: 28,
                      height: 28,
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      backgroundColor: "#f97316",
                      color: "white",
                    }}
                  >
                    {getInitials()}
                  </Avatar>
                  <Box
                    sx={{
                      display: { xs: "none", sm: "block" },
                      textAlign: "left",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.8rem",
                        fontWeight: 700,
                        color: "#1e293b",
                        lineHeight: 1.1,
                      }}
                    >
                      {admin?.name || "Administrator"}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.65rem",
                        fontWeight: 500,
                        color: "#64748b",
                        lineHeight: 1.1,
                      }}
                    >
                      {admin?.role ? admin.role.toUpperCase() : "ADMIN"}
                    </Typography>
                  </Box>
                </Stack>
              </Button>
            </Tooltip>

            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              keepMounted
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              sx={{
                mt: 1.5,
                "& .MuiPaper-root": {
                  backgroundColor: "white",
                  border: "1px solid #e2e8f0",
                  boxShadow:
                    "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
                  borderRadius: 3,
                  minWidth: "200px",
                  p: 1,
                },
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  borderBottom: "1px solid #f1f5f9",
                  mb: 1,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.85rem",
                    fontWeight: 700,
                    color: "#0f172a",
                  }}
                >
                  {admin?.name || "Administrator"}
                </Typography>
                <Typography sx={{ fontSize: "0.75rem", color: "#64748b" }}>
                  {admin?.email}
                </Typography>
              </Box>
              {/* <MenuItem onClick={handleClose} sx={{ color: '#475569', borderRadius: 2, py: 1, fontSize: '0.85rem', fontWeight: 500 }}>
                Profile Details
              </MenuItem> */}
              <MenuItem
                onClick={handleLogout}
                sx={{
                  color: "#ef4444",
                  borderRadius: 2,
                  py: 1,
                  fontSize: "0.85rem",
                  fontWeight: 600,
                }}
              >
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Stack>
      </Toolbar>

      {/* Share Feedback Dialog */}
      <Dialog
        open={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          Share Feedback
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Let us know your thoughts on how we can improve the Kinnow
            Dashboard.
          </Typography>

          <Box
            sx={{ display: "flex", gap: 1, mb: 3, justifyContent: "center" }}
          >
            {[1, 2, 3, 4, 5].map((star) => (
              <IconButton
                key={star}
                onClick={() => setFeedbackRating(star)}
                sx={{ color: star <= feedbackRating ? "#fbbf24" : "#cbd5e1" }}
              >
                ★
              </IconButton>
            ))}
          </Box>

          <TextField
            autoFocus
            label="Your feedback"
            type="text"
            fullWidth
            multiline
            rows={3}
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            placeholder="Type your suggestions here..."
            sx={{ borderRadius: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setFeedbackOpen(false)}>Cancel</Button>
          <Button
            onClick={() => {
              alert("Thank you for your feedback!");
              setFeedbackOpen(false);
              setFeedbackText("");
            }}
            variant="contained"
            disabled={!feedbackText.trim()}
          >
            Submit Feedback
          </Button>
        </DialogActions>
      </Dialog>
    </AppBar>
  );
};

export default Header;
